/**
 * Transaction History Collection
 */

define(function(require, exports, module) {
    'use strict';

    const $ = require('j');
    const i18n = require('i18n');
    const core = require('core');
    const user = require('user');
    const _ = require('underscore');
    const server = require('server');
    const csvUtil = require('util/csv.util');
    const BaseCollection = require('common/base.collection');
    const TransactionHistoryItemModel = require('portlets/cashier/transaction-history/transaction-history.item.model');
    const TransactionHistoryItemDetailedModel =
        require('portlets/cashier/transaction-history/transaction-history.item-detailed.model');

    module.exports = BaseCollection.extend({

        /**
         * page number that will be shown with out pressing load more button
         * @type {Number}
         */
        pageSize: 30,

        /**
         * Stores paging data
         * @type {String}
         */
        cachedPageId: null,

        /**
         * Stores summary transaction amounts
         * @type {Object}
         */
        summaryAmounts: null,

        initialize: function(config) {
            this.isDetailed = Boolean(config.isDetailedView);
            this.summaryAmounts = config.isShowTotalTransactionHistory ? {} : null;
        },

        model: function(model, options) {
            if (options.collection.isDetailed) {
                return new TransactionHistoryItemDetailedModel(model);
            }
            return new TransactionHistoryItemModel(model);
        },

        comparator: function(model) {
            return -new Date(model.get('transactionDateInUms')).getTime();
        },

        sync: function(method, collection, options) {
            if (method === 'read') {
                let state;

                if (this.isDetailed) {
                    state = this.getDetailedTransaction(options);
                    this.dirtyHackForSummary(options);
                } else {
                    state = this.getTransactions(options);
                }

                return state.done(function(response) {
                        if (response.data.pagerResponse) {
                            this.cachedPageId = response.data.pagerResponse.nextPageId;
                        }
                        this.startDate = options.startDate;
                        this.endDate = options.endDate;
                        options.success(response);
                    }.bind(this))
                    .fail(options.error);
            }
        },

        getTransactions: function(options) {
            const { startDate, endDate } = options;
            this.lastStartEndDates = { startDate, endDate };

            var pagerRequest = {
                pageSize: this.pageSize,
                returnTotalCount: true
            };

            if (_.has(options, 'remove') && options.remove === false) {
                pagerRequest.pageId = this.cachedPageId;
            }

            this._setTotalAmountsTime(options);

            return server.send({
                ID: 32012,
                responses: [32013, 32014],
                description: 'Get Balance History',
                startDate: window.Playtech.API.formatter.getDateForIms(startDate),
                endDate: window.Playtech.API.formatter.getDateForIms(endDate),
                ignorePlayerViewConf: false,
                transactionGroups: options.group === 'all' ? [] : [options.group],
                pagerRequest: pagerRequest,
                orderByDate: 'desc'
            });
        },

        getAllTransactionsForExport: function(options) {
            const pagerRequest = {
                returnAllPageIds: true,
                pageSize: 100,
                returnTotalCount: true
            };
            let allTransactions = {};
            const state = $.Deferred();

            const fetchTransactions = (pageId) => {
                server.send({
                    ID: 32012,
                    responses: [32013, 32014],
                    description: 'Get Balance History',
                    startDate: window.Playtech.API.formatter.getDateForIms(this.startDate),
                    endDate: window.Playtech.API.formatter.getDateForIms(this.endDate),
                    ignorePlayerViewConf: false,
                    transactionGroups: options.group === 'all' ? [] : [options.group],
                    pagerRequest: {
                        ...pagerRequest,
                        ...pageId && { pageId }},
                    orderByDate: 'desc'
                })
                    .then(response => {
                        if (pageId) {
                            allTransactions = {
                                ...allTransactions,
                                data: {
                                    ...allTransactions.data,
                                    walletTransactions: allTransactions.data.walletTransactions.concat(response.data.walletTransactions)
                                }
                            };
                        } else {
                            allTransactions = response;
                        }

                        if (response.data?.pagerResponse?.nextPageId) {
                            fetchTransactions(response.data.pagerResponse.nextPageId);
                        } else {
                            const modelOptions = { collection: { isDetailed: false } };
                            const transactionsToExport = this.parse(allTransactions)
                                .map(transaction => this.model(transaction, modelOptions).toJSON({ forCsv: true }));

                            state.resolve(transactionsToExport);
                        }
                    })
                    .fail(error => {
                        state.reject(error);
                    });
            };

            fetchTransactions();

            return state;
        },

        getDetailedTransaction: function(options) {
            var request = {
                ID: 33026,
                responses: [33027, 33028],
                description: 'Get Detailed Transaction History',
                startTime: options.startDate.toISOString().replace(/\..+/g, '') + 'Z',
                endTime: options.endDate.toISOString().replace(/\..+/g, '') + 'Z',
                cancellations: options.cancellations || true
            };

            this._setTotalAmountsTime(options);

            if (options.transactionType !== 'all') {
                _.extend(request, {
                    type: options.transactionType
                });
            }

            if (options.transactionStatus !== 'all') {
                _.extend(request, {
                    status: options.transactionStatus
                });
            }

            return server.send(request);
        },

        parse: function(response) {
            var data = response.data;

            if (data.payments) {
                return this._parseDetailedTransactions(response);
            }

            data.summary && this._setTotalAmountsSummary(data.summary);

            return _.chain(data.walletTransactions)
                .each(function(transaction) {
                    const defaultType = data.defaultTypes.find((type) => type.codeName === transaction.actionType);

                    transaction.typeName = defaultType?.name || '';
                    transaction.description = this._getTransactionDescription(
                        transaction.templateTags, defaultType?.description
                    );
                    transaction.transactionDateInUms = window.Playtech.API.formatter.parseImsDate(transaction.transactionDateInUms);
                }.bind(this))
                .map(function(transaction) {
                    if (transaction.regulatorData) {
                        return _.extend({}, _.omit(transaction, 'regulatorData'), transaction.regulatorData);
                    }
                    return transaction;
                })
                .map(function(transaction) {
                    if (transaction.participationStartDate) {
                        transaction.participationStartDate =
                            window.Playtech.API.formatter.parseImsDate(transaction.participationStartDate, true);
                    }
                    return transaction;
                })
                .value();
        },

        /**
         * Formatting transaction description
         * @param {Object} templateTags
         * @param {String} [description]
         * @returns {String} - formatted template
         * @private
         */
        _getTransactionDescription: function(templateTags = {}, description = '') {
            const tags = description.match(/<.+?>/g) || [];

            if (!tags.length) {
                return description;
            }

            let isTagsMatched = true;
            let formattedDescription = description;

            for (let i = 0; i < tags.length; i++) {
                const parsedTag = tags[i].replace('<', '').replace('>', '');

                if (templateTags[parsedTag]) {
                    formattedDescription = formattedDescription.replace(tags[i], templateTags[parsedTag]);
                } else {
                    isTagsMatched = false;
                    break;
                }
            }

            return isTagsMatched ? formattedDescription : '';
        },

        _parseDetailedTransactions: function(response) {
            var data = response.data;
            const { formatCurrency, getDateTimeShort } = window.Playtech.API.formatter;

            return _.map(data.payments.transaction, function(transaction) {
                transaction.amountWithCurrency = formatCurrency(transaction.amount);
                transaction.date = getDateTimeShort(transaction.time);
                transaction.transactionDateInUms = getDateTimeShort(transaction.time);

                this._trimDotpayCardAccount(transaction.method);

                this._setTypeAndStatusTranslation(transaction);

                return transaction;
            }.bind(this));
        },

        _trimDotpayCardAccount: function(method) {
            if (method && method.code === 'DotpayCard' && method.account) {
                method.account.forEach(function(item) {
                    if (item.specificParams) {
                        item.specificParams.accountId = '*' + item.specificParams.accountId.substr(-4);
                    }
                });
            }
        },

        _setTypeAndStatusTranslation: function(transaction) {
            var translationType = 'playtech.mobile.system.mobile-cashier.transactions.' + transaction.type;

            transaction.actionType = transaction.typeName = translationType !== i18n.t(translationType)
                ? i18n.t(translationType)
                : transaction.type;
        },

        /**
         * Prepare data for CSV file
         * @returns {string} content
         */
        prepareTransactionData: function(models) {
            if (!this.length) {
                return csvUtil.jsonToCsv({});
            }

            var existingColumnTitles = [];
            var transactionTable = (models || this)
                .map(function(model) {
                    var transaction = 'toJSON' in model ? model.toJSON({ forCsv: true }) : model;
                    var columns = {};

                    var getRegularTransactionHeading = function() {
                        return {
                            'Date and Time': transaction.transactionDateInUms,
                            'Transaction name': transaction.typeName,
                            'Amount': transaction.amount,
                            'Balance': transaction.balance,
                            'Product': transaction.clientType,
                            'Client Platform': transaction.clientPlatform,
                            'Description': transaction.description
                        };
                    };
                    var getDetailedTransactionHeading = function() {
                        var account = transaction.method.account
                            ? transaction.method.account[0].specificParams.accountId
                            : transaction.method.code;
                        return {
                            'Date and time': transaction.date,
                            'Transaction name': transaction.type,
                            'Amount': transaction.amountWithCurrency,
                            'Status': transaction.status,
                            'Method': transaction.method.code,
                            'Account': account,
                            'Transaction ID': transaction.code
                        };
                    };

                    _.each(this.isDetailed ? getDetailedTransactionHeading() : getRegularTransactionHeading(),
                        function(value, title) {
                            if (!_.isUndefined(value)) {
                                if (!existingColumnTitles.includes(title)) {
                                    existingColumnTitles.push(title);
                                }
                                columns[title] = value;
                            }
                        }
                    );

                    return columns;
                }, this)
                .map(function(columns) {
                    return _.reduce(
                        existingColumnTitles,
                        function(formattedColumns, title) {
                            formattedColumns[title] = _.isUndefined(columns[title])
                                ? ''
                                : columns[title];

                            return formattedColumns;
                        },
                        {}
                    );
                });

            return transactionTable;
        },

        /**
         * generate CSV file from collection
         * @returns {string} CSV file content
         */
        generateCsv: function(models) {
            const name = user.get('userName');

            return csvUtil.jsonToCsv(this.prepareTransactionData(models),[{[`Player: ${name}`]: ''}], this.prepareSummaryAmounts());
        },

        getSummaryAmounts: function() {
            var summary = this.summaryAmounts;
            return summary ? _.extend({}, summary) : null;
        },

        _setTotalAmountsTime: function(options) {
            _.extend(this.summaryAmounts, {
                startDate: window.Playtech.API.formatter.getDate(window.Playtech.API.formatter.getDateForIms(options.startDate)),
                endDate: window.Playtech.API.formatter.getDate(window.Playtech.API.formatter.getDateForIms(options.endDate))
            });
        },

        _setTotalAmountsSummary: function(summary) {
            var summaryAmounts = this.summaryAmounts;
            var data;

            if (!summaryAmounts) {
                return;
            }

            data = _.mapObject(summary, function(val) {
                return window.Playtech.API.formatter.formatCurrency(val.amount, {
                    currencyCode: val.currencyCode
                });
            });

            _.extend(summaryAmounts, data);
        },

        /**
         * Prepare data for CSV file
         * @returns {string} content
         */
        prepareSummaryAmounts: function() {
            var summary = this.getSummaryAmounts();
            var result = {};
            var startDate, endDate;

            if (!summary) {
                return;
            }

            startDate = summary.startDate;
            endDate = summary.endDate;

            function getTranslationVal(key) {
                var keyTmpl = 'playtech.mobile.system.mobile-cashier.transaction-history.transaction-total.';
                return i18n.t(keyTmpl + key);
            }

            result[getTranslationVal('title')] = startDate === endDate ? startDate : [startDate, endDate].join(' - ');
            result[getTranslationVal('wins')] = summary.totalWins;
            result[getTranslationVal('bets')] = summary.totalBets;
            result[getTranslationVal('deposit')] = summary.totalDeposits;
            result[getTranslationVal('withdraw')] = summary.totalApprovedWithdraws;

            return [result];
        },

        // TODO: remove after release new request for detailed history
        dirtyHackForSummary: function(options) {
            this.getTransactions(_.extend({}, options, {
                group: 'all'
            })).done(function(resp) {
                var summary = resp.data.summary;
                summary && this._setTotalAmountsSummary(summary);
                core.trigger('transaction-total:update');
            }.bind(this));
        }
    });
});
